import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, addData, updateData, deleteData, getseriesType } from '../../../services/event/abstractReceipt/programManagerServices';
import { Modal, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import Swal from 'sweetalert2';
const subDomain = localStorage.getItem('sub_domain') || '';

const Programmanager = () => {
    const [dataList, setDataList] = useState([]);
    const [form, setForm] = useState({ id: '', sub_domain: subDomain, series: '', conference_committee: '', conf_shortname: '', expertise: '', coordinator_name: '', designation: '', config_email: '', status: 0 });
    const [modalType, setModalType] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const isSingleEntryExist = dataList.length >= 1;
    const [seriesValue, setSeriesValue] = useState('');

    // Fetch data list
    const fetchData = useCallback(async () => {
        try {
            const data = await getData({ sub_domain: subDomain });
            setDataList(data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        }
    }, []);

    const fetchseriesType = useCallback(async () => {
      try {
        const data = await getseriesType({ sub_domain: subDomain });
        if (data && data.length > 0) {
          const seriesInfo = data[0];
          setSeriesValue(seriesInfo.series || '');
        }
      } catch (error) {
        console.error('Failed to fetch series info:', error);
      }
    }, []);

useEffect(() => {
    const fetchDataAsync = async () => {
        setLoading(true); 
        await fetchData();
        await fetchseriesType();   
        // Optional 2 second delay
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setLoading(false); 
      };
    
      fetchDataAsync();
}, [fetchData, fetchseriesType]);

    // ✅ Updated handleChange (status parsing + uppercase logic)
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: name === "conference_committee" ? value.toUpperCase() :
                    name === "status" ? parseInt(value) : value,
        }));
        // Clear error when user types
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            'conference_committee',
            'conf_shortname',
            'expertise',
            'coordinator_name',
            'designation',
            'config_email'
        ];

        requiredFields.forEach((field) => {
            if (!form[field]?.trim()) {
                newErrors[field] = 'Please fill out this field';
            }
        });

        // Email validation
        if (form.config_email) {
            const parts = form.config_email.split("@");

            if (parts.length !== 2) {
                newErrors.config_email = "Invalid email format";
            } else {
                const domain = parts[1];

                const allowedDomains = [
                    "scientexconferences.com",
                    "scientexconferences.org",
                    "scientexconference.com",
                    "scientexconference.org",
                    "scientexevents.org",
                    "scientexevents.com"
                ];

                if (!allowedDomains.includes(domain)) {
                    newErrors.config_email = "Email must belong to Scientex domain";
                }
            }
        } else {
            newErrors.config_email = "Please fill out this field";
        }

        // ✅ Conference shortname validation (Capital + lowercase + optional space + digits)
        if (form.conf_shortname && !/^[A-Z][a-zA-Z]*(?: [A-Za-z0-9]+)*$/.test(form.conf_shortname)) {
        newErrors.conf_shortname =
            'Shortname must start with a capital letter and can include letters, numbers, and spaces (e.g., Surgery 2025)';
        }
        
        // ✅ Coordinator name validation
        if (form.coordinator_name && !/^[A-Z][a-z]+(?: [A-Z][a-z]+)*$/.test(form.coordinator_name)) {
        newErrors.coordinator_name =
            'Coordinator name must start with a capital letter for each word (e.g., Luis Zane)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ✅ Updated handleSubmit: properly sets status to 0 when editing if it was 1
    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            let updatedForm = { ...form };

            if (modalType === 'edit') {
                updatedForm.status = 0;
            }

            if (modalType === 'add') {
                await addData(updatedForm);
            } else if (modalType === 'edit') {
                await updateData(updatedForm);
                console.log('updatedForm', updatedForm);
            }

            fetchData();
            setShowModal(false);
            // ✅ Show success alert with auto-close
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: `Conference Manager ${modalType === 'add' ? 'added' : 'updated'} successfully!`,
                timer: 2000, // 2 seconds
                showConfirmButton: false,
              });
        
            } catch (error) {
              console.error('Error during form submission:', error);
              Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Something went wrong while submitting the form!',
              });
            }
    };

    const handleDelete = async () => {
        if (selectedData) {
            setDataList((prev) => prev.filter((data) => data.id !== selectedData.id));
            await deleteData(selectedData);
            setShowDeleteConfirmModal(false);
        }
    };

    // ✅ Make sure status is always present
    const openModal = (type, data = null) => {
        setErrors({}); // ✅ Clear previous errors
        setModalType(type);
        setForm(data);
        if (type === 'edit' && data) {
            setForm({
                ...data,
                status: data.status ?? 0,
            });
        } else {
            setForm({
                id: '',
                sub_domain: subDomain,
                series: seriesValue || '',
                conference_committee: '',
                conf_shortname: '',
                expertise: '',
                coordinator_name: '',
                designation: 'Conference Manager',
                config_email: '',
                status: 0
            });
        }

        setShowModal(true);
    };

    const openDeleteConfirmModal = (data) => {
        setSelectedData(data);
        setShowDeleteConfirmModal(true);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const filteredData = useMemo(() => {
        return dataList.filter(
            (data) =>
                data.sub_domain.toLowerCase().includes(searchText.toLowerCase()) ||
                data.expertise.toLowerCase().includes(searchText.toLowerCase())
        ).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)); // Descending by updatedAt
    }, [dataList, searchText]);

    const columns = [
        {
            name: '#',
            selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
            sortable: false,
            width: '80px',
        },
        {
            name: 'Conference Committee',
            selector: (row) => row.conference_committee,
            sortable: true,
        },
        {
            name: 'Short Name',
            selector: (row) => row.conf_shortname,
            sortable: true,
        },
        {
            name: 'Status',
            cell: (row) => (
              <span className={`badge ${Number(row.status) === 1 ? 'bg-success' : 'bg-danger'}`}>
                {Number(row.status) === 1 ? 'Active' : 'Inactive'}
              </span>
            ),
            sortable: true,
          },
        {
            name: 'Actions',
            cell: (row) => (
                <div>
                    <Button className="me-2" variant="warning" size="sm" onClick={() => openModal('edit', row)}>Edit</Button>
                    <Button variant="danger" size="sm" onClick={() => openDeleteConfirmModal(row)}>Delete</Button>
                </div>
            ),
        },
    ];
                    
return (
    <div className="d-flex">
    <Sidebar />
    <div className="main-content w-100">
        <Header />
        {loading ? (
                        <div
                          className="d-flex justify-content-center align-items-center w-100"
                          style={{ minHeight: "300px" }}
                        >
                          <Spinner /> {/* Centered Spinner */}
                        </div>
                                ) :(
                <div>
        <div className="container-fluid p-4">
        <div className="row">
            <div className="col-8">
            <p className='fs-4'>Receipt Generation / Abstract Receipt / Configurations / Conference Manager</p>
            </div>
            <div className="col-4 text-end">
                {!isSingleEntryExist && (
                    <button className="btn btn-primary mb-3" onClick={() => openModal('add')}>
                    Add
                    </button>
                )}
            </div>
        </div>

        <DataTable
                        columns={columns}
                        data={filteredData}
                        pagination
                        search
                        highlightOnHover
                        responsive
                        striped
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
                        onChangePage={handlePageChange}
                        subHeader
                        subHeaderComponent={
                            <div>
                                <input className="form-control" type="text" placeholder="Search" value={searchText}
                                    onChange={handleSearchChange}
                                />
                            </div>
                        }
                    />

                    {/* Modal for Add/Edit data */}
                    <Modal show={showModal} className='modal-lg' onHide={() => setShowModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>{modalType === 'add' ? 'Add' : 'Edit'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <div className='row'>
                                    <Form.Group className="col-md-2 mb-3">
                                        <Form.Label>Subdomain</Form.Label>
                                        <Form.Control type="text" name="sub_domain" value={form.sub_domain} onChange={handleChange} readOnly />
                                    </Form.Group>
                                     <Form.Group className="col-md-2 mb-3">
                                        <Form.Label>series</Form.Label>
                                        <Form.Control type="text" name="series" value={form.series} onChange={handleChange} readOnly />
                                    </Form.Group>
                                    <Form.Group className="col-md-4 mb-3">
                                        <Form.Label>Conference Committee<small className="text-danger">*</small></Form.Label>
                                        <Form.Control type="text" name="conference_committee" value={form.conference_committee} onChange={handleChange} isInvalid={!!errors.conference_committee} />
                                        <Form.Control.Feedback type="invalid">  {errors.conference_committee} </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="col-md-4 mb-3">
                                        <Form.Label>Conference Shortname<small className="text-danger">*</small></Form.Label>
                                        <Form.Control type="text" name="conf_shortname" value={form.conf_shortname} onChange={handleChange} isInvalid={!!errors.conf_shortname} />
                                        <Form.Control.Feedback type="invalid">  {errors.conf_shortname} </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="col-md-6 mb-3">
                                        <Form.Label>Subject/Expertise<small className="text-danger">*</small></Form.Label>
                                        <Form.Control type="text" name="expertise" value={form.expertise} onChange={handleChange} isInvalid={!!errors.expertise} />
                                        <Form.Control.Feedback type="invalid">  {errors.expertise} </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="col-md-6 mb-3">
                                        <Form.Label>Coordinator Name<small className="text-danger">*</small></Form.Label>
                                        <Form.Control type="text" name="coordinator_name" value={form.coordinator_name} onChange={handleChange} isInvalid={!!errors.coordinator_name} />
                                        <Form.Control.Feedback type="invalid">  {errors.coordinator_name} </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="col-md-4 mb-3">
                                        <Form.Label>Designation<small className="text-danger">*</small></Form.Label>
                                        <Form.Control type="text" name="designation" value={form.designation} onChange={handleChange} isInvalid={!!errors.designation} readOnly/>
                                        <Form.Control.Feedback type="invalid">{errors.designation}</Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="col-md-4 mb-3">
                                        <Form.Label>Email<small className="text-danger">*</small></Form.Label>
                                        <Form.Control type="email" name="config_email" value={form.config_email} onChange={handleChange} isInvalid={!!errors.config_email} />
                                        <Form.Control.Feedback type="invalid">  {errors.config_email} </Form.Control.Feedback>
                                    </Form.Group>                                 
                                </div>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                            <Button variant="primary" onClick={handleSubmit}>{modalType === 'add' ? 'Add' : 'Save'}</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Delete Confirmation Modal */}
                    <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>Delete</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Are you sure you want to delete this row?</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>Cancel</Button>
                            <Button variant="danger" onClick={handleDelete}>Yes, Delete</Button>
                        </Modal.Footer>
                    </Modal>
                </div>
           <Footer />
        </div>
      )};
    </div>
    </div>
);
};

export default Programmanager;
